import { useState } from 'react';
import { Plus, Edit2, Trash2, ArrowRight, GripVertical, X, Check, Info } from 'lucide-react';
import { ROUTING_RULES, PLATFORMS } from '../data/mockData';

function PlatformBadge({ id }) {
  const p = PLATFORMS.find(x => x.id === id);
  if (!p) return <span className="text-muted">—</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 22, height: 22, borderRadius: 4, background: p.bg, color: p.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 800 }}>{p.abbr}</span>
      <span style={{ fontSize: 12, color: 'var(--muted)' }}>{p.name}</span>
    </span>
  );
}

const ACTION_BADGE = {
  route:    <span className="badge badge-blue">Route</span>,
  failover: <span className="badge badge-amber">Failover</span>,
  block:    <span className="badge badge-red">Block</span>,
};

function RuleModal({ rule, onClose, onSave }) {
  const isNew = !rule;
  const [form, setForm] = useState({
    name: rule?.name || '',
    pattern: rule?.pattern || '',
    platform: rule?.platform || '',
    priority: rule?.priority || 10,
    action: rule?.action || 'route',
    active: rule?.active ?? true,
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{isNew ? 'Add Routing Rule' : 'Edit Routing Rule'}</div>
          <button className="btn btn-secondary btn-xs" onClick={onClose}><X size={12}/></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info">
            <Info size={14} style={{ flexShrink: 0 }}/>
            <span>Rules are evaluated in priority order (lower number = higher priority). Use X as a single-digit wildcard, * as multi-digit.</span>
          </div>

          <div className="form-group">
            <label className="form-label">Rule Name</label>
            <input className="form-input" placeholder="e.g. HQ Main Lines" value={form.name} onChange={e => setForm({...form, name: e.target.value})}/>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Number Pattern (E.164)</label>
              <input className="form-input font-mono" placeholder="+1415555XXXX" value={form.pattern} onChange={e => setForm({...form, pattern: e.target.value})}/>
              <div className="form-hint">X = any digit, * = wildcard</div>
            </div>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <input className="form-input" type="number" min={1} max={99} value={form.priority} onChange={e => setForm({...form, priority: parseInt(e.target.value)})}/>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Route to Platform</label>
            <select className="form-input select-input form-select" value={form.platform} onChange={e => setForm({...form, platform: e.target.value})}>
              <option value="">— Select platform —</option>
              {PLATFORMS.filter(p => p.status === 'connected').map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.type})</option>
              ))}
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Action</label>
              <select className="form-input select-input form-select" value={form.action} onChange={e => setForm({...form, action: e.target.value})}>
                <option value="route">Route</option>
                <option value="failover">Failover</option>
                <option value="block">Block</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select className="form-input select-input form-select" value={form.active ? 'active' : 'inactive'} onChange={e => setForm({...form, active: e.target.value === 'active'})}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(form)}>
            <Check size={13}/> {isNew ? 'Create Rule' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Routing() {
  const [rules, setRules] = useState(ROUTING_RULES);
  const [editTarget, setEditTarget] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [testNumber, setTestNumber] = useState('');
  const [testResult, setTestResult] = useState(null);

  const testRoute = () => {
    const match = rules
      .filter(r => r.active)
      .sort((a, b) => a.priority - b.priority)
      .find(r => {
        const regex = r.pattern
          .replace(/\*/g, '.*')
          .replace(/X/g, '\\d')
          .replace(/\[/g, '[')
          .replace(/\]/g, ']');
        try { return new RegExp(`^${regex}$`).test(testNumber); } catch { return false; }
      });
    setTestResult(match || null);
  };

  const handleSave = (form) => {
    if (editTarget) {
      setRules(prev => prev.map(r => r.id === editTarget.id ? { ...r, ...form } : r));
    } else {
      setRules(prev => [...prev, { id: `r${Date.now()}`, ...form }]);
    }
    setEditTarget(null);
    setShowNew(false);
  };

  const toggleActive = (id) => setRules(prev => prev.map(r => r.id === id ? { ...r, active: !r.active } : r));
  const deleteRule   = (id) => setRules(prev => prev.filter(r => r.id !== id));

  return (
    <div className="content">
      <div className="grid-2 mb-20">
        {/* Rule tester */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Route Tester</div>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Test E.164 Number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-input font-mono"
                  placeholder="+14155550102"
                  value={testNumber}
                  onChange={e => setTestNumber(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && testRoute()}
                  style={{ flex: 1 }}
                />
                <button className="btn btn-primary" onClick={testRoute}>
                  <ArrowRight size={13}/> Test
                </button>
              </div>
            </div>
            {testResult === undefined && null}
            {testResult && (
              <div className="alert alert-success">
                <Check size={14}/>
                <div>
                  <div style={{ fontWeight: 600, marginBottom: 2 }}>Matches: {testResult.name}</div>
                  <div style={{ fontSize: 12 }}>
                    Pattern <code style={{ background: 'var(--border)', padding: '0 4px', borderRadius: 3 }}>{testResult.pattern}</code> → {PLATFORMS.find(p=>p.id===testResult.platform)?.name || testResult.platform} (priority {testResult.priority})
                  </div>
                </div>
              </div>
            )}
            {testResult === null && testNumber && (
              <div className="alert alert-warn">
                <X size={14}/>
                <span>No matching rule found. Call would be rejected or fall to default.</span>
              </div>
            )}
          </div>
        </div>

        {/* Summary */}
        <div className="card">
          <div className="card-header"><div className="card-title">Platform Routing Summary</div></div>
          <div className="card-body">
            {PLATFORMS.filter(p => p.status === 'connected').map(p => {
              const count = rules.filter(r => r.platform === p.id && r.active).length;
              return (
                <div key={p.id} className="trunk-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 26, height: 26, borderRadius: 6, background: p.bg, color: p.color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{p.abbr}</span>
                    <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{p.name}</span>
                    <span className={`badge ${p.type === 'UC' ? 'badge-blue' : 'badge-purple'}`}>{p.type}</span>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--muted-2)' }}>{count} rule{count !== 1 ? 's' : ''}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Routing Rules</div>
            <div className="card-sub">Evaluated in priority order — lower number wins</div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNew(true)}>
            <Plus size={13}/> Add Rule
          </button>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}></th>
                <th>Rule Name</th>
                <th>Number Pattern</th>
                <th>Platform</th>
                <th>Action</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.sort((a, b) => a.priority - b.priority).map(r => (
                <tr key={r.id} style={{ opacity: r.active ? 1 : .45 }}>
                  <td><GripVertical size={14} color="var(--muted-3)" style={{ cursor: 'grab' }}/></td>
                  <td style={{ fontWeight: 500, color: 'var(--text-2)' }}>{r.name}</td>
                  <td><span className="tag font-mono">{r.pattern}</span></td>
                  <td><PlatformBadge id={r.platform} /></td>
                  <td>{ACTION_BADGE[r.action]}</td>
                  <td><span style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>{r.priority}</span></td>
                  <td>
                    <span
                      className={`badge ${r.active ? 'badge-green' : 'badge-gray'}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleActive(r.id)}
                    >
                      {r.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-secondary btn-xs" onClick={() => setEditTarget(r)}>
                        <Edit2 size={11}/> Edit
                      </button>
                      <button className="btn btn-danger btn-xs" onClick={() => deleteRule(r.id)}>
                        <Trash2 size={11}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {(editTarget || showNew) && (
        <RuleModal
          rule={editTarget}
          onClose={() => { setEditTarget(null); setShowNew(false); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
